import React, { useState, useEffect, useMemo } from 'react';

import ContactListContent from './ContactListContent';
import ContactListEmptyState from './ContactListEmptyState';
import ContactListErrorState from './ContactListErrorState';
import ContactListSkeleton from './ContactListSkeleton';
import useContactData from './useContactData';
import useContactSearch from './useContactSearch';
import { APP_CONFIG } from '../../../configs/config';

const CONTACTS_PER_PAGE = 10;

export default function ContactList() {
  const {
    isLoading,
    isAuthenticated,
    hasError,
    errorMessage,
    contacts,
    refetch,
  } = useContactData();

  const { searchQuery, setSearchQuery, filteredContacts } =
    useContactSearch(contacts);

  const [currentPage, setCurrentPage] = useState(1);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredContacts.length / CONTACTS_PER_PAGE);
  const startIndex = (currentPage - 1) * CONTACTS_PER_PAGE;
  const endIndex = startIndex + CONTACTS_PER_PAGE;
  const paginatedContacts = useMemo(
    () => filteredContacts.slice(startIndex, endIndex),
    [filteredContacts, startIndex, endIndex]
  );

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  if (!isAuthenticated) {
    return (
      <ContactListErrorState
        title="Sign in required"
        message="Sign in through the main TadReamk portal to view extracted contacts."
        primaryActionLabel="Go to Sign In"
        onPrimaryAction={() => {
          const returnUrl = encodeURIComponent(window.location.href);
          window.location.href = `${APP_CONFIG.MAIN_WEBSITE_ORIGIN}/signin?returnUrl=${returnUrl}`;
        }}
      />
    );
  }

  if (isLoading) {
    return <ContactListSkeleton />;
  }

  if (hasError) {
    return (
      <ContactListErrorState
        title="Unable to load contacts"
        message={errorMessage}
        primaryActionLabel="Retry"
        onPrimaryAction={refetch}
      />
    );
  }

  if (!contacts.length) {
    return <ContactListEmptyState onRefresh={refetch} />;
  }

  return (
    <div className="space-y-6">
      {/* Total Contacts */}
      <div className="flex flex-col gap-2">
        <p className="text-sm uppercase tracking-widest text-violet-300 font-semibold">
          Total contacts
        </p>
        <p className="text-3xl font-semibold text-white">{filteredContacts.length}</p>
        {filteredContacts.length > CONTACTS_PER_PAGE && (
          <p className="text-sm text-gray-400">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredContacts.length)} of {filteredContacts.length}
          </p>
        )}
      </div>

      {/* Search Area */}
      <div className="flex flex-row gap-3">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Search contacts by name, email, organization..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-3 pl-11 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          <svg
            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35" />
          </svg>
        </div>
        <button
          type="button"
          onClick={() => setSearchQuery('')}
          className="px-6 py-3 rounded-2xl font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 shadow-lg transition-all hover:-translate-y-0.5 whitespace-nowrap"
        >
          Clear
        </button>
      </div>

      {filteredContacts.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-400 text-lg">No contacts found matching "{searchQuery}"</p>
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="mt-4 text-violet-300 hover:text-violet-200 font-medium"
          >
            Clear search
          </button>
        </div>
      ) : (
        <>
          <ContactListContent contacts={paginatedContacts} />
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-8">
              {/* Previous Button */}
              <button
                type="button"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded-xl font-medium transition-colors ${
                  currentPage === 1
                    ? 'bg-white/5 text-gray-500 cursor-not-allowed'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                Previous
              </button>

              {/* Page Numbers */}
              <div className="flex gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    type="button"
                    onClick={() => handlePageClick(page)}
                    className={`w-10 h-10 rounded-xl font-semibold transition-all ${
                      currentPage === page
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              {/* Next Button */}
              <button
                type="button"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 rounded-xl font-medium transition-colors ${
                  currentPage === totalPages
                    ? 'bg-white/5 text-gray-500 cursor-not-allowed'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}


